import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditOfferPage = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    fetchOffer();
  }, [offerId]);

  const fetchOffer = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/offers/${offerId}`);
      setOffer(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'offre');
      console.error('Error fetching offer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/offers/${offerId}`, {
        title: offer.title,
        client_info: offer.client_info,
        suppliers: offer.suppliers,
        advisor_info: offer.advisor_info,
        agency_name: offer.agency_name,
      });
      toast.success('Offre mise à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error('Error saving offer:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateClientInfo = (field, value) => {
    setOffer({
      ...offer,
      client_info: {
        ...offer.client_info,
        [field]: value,
      },
    });
  };

  const updateSupplier = (index, field, value) => {
    if (!offer || !offer.suppliers) return;
    const newSuppliers = [...offer.suppliers];
    newSuppliers[index] = {
      ...newSuppliers[index],
      [field]: parseFloat(value) || value,
    };
    setOffer({ ...offer, suppliers: newSuppliers });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Offre non trouvée</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="mb-2 rounded-sm"
                data-testid="back-btn"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour
              </Button>
              <h1 className="text-4xl font-heading tracking-tight">Éditer l'offre</h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              size="lg"
              className="rounded-sm"
              data-testid="save-offer-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-5 w-5" />
                  Enregistrer
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Energy Type Badge */}
        <div className="mb-6">
          <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
            offer.energy_type === 'electricity' 
              ? 'bg-blue-100 text-blue-800 border border-blue-300' 
              : 'bg-orange-100 text-orange-800 border border-orange-300'
          }`}>
            {offer.energy_type === 'electricity' ? '⚡ Électricité' : '🔥 Gaz naturel'}
          </span>
        </div>

        {/* Title */}
        <Card className="mb-6 border border-border rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading">Titre de l'offre</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              data-testid="offer-title-input"
              value={offer.title}
              onChange={(e) => setOffer({ ...offer, title: e.target.value })}
              className="rounded-sm"
            />
          </CardContent>
        </Card>

        {/* Personalization */}
        <Card className="mb-6 border border-border rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading">Personnalisation de l'offre</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nom du client</Label>
              <Input
                value={offer.client_info?.name || ''}
                onChange={(e) => updateClientInfo('name', e.target.value)}
                className="rounded-sm"
                placeholder="Ex: M. Martin, Mme Dupont"
              />
            </div>
            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom du conseiller</Label>
                <Input
                  value={offer.advisor_info?.name || ''}
                  onChange={(e) => setOffer({
                    ...offer,
                    advisor_info: { 
                      name: e.target.value,
                      phone: offer.advisor_info?.phone || '',
                      email: offer.advisor_info?.email || ''
                    }
                  })}
                  className="rounded-sm"
                  placeholder="Ex: Jean Dupont"
                />
              </div>
              <div>
                <Label>Nom de l'agence</Label>
                <Input
                  value={offer.agency_name || ''}
                  onChange={(e) => setOffer({ ...offer, agency_name: e.target.value })}
                  className="rounded-sm"
                  placeholder="Ex: Place des Énergies"
                />
              </div>
              <div>
                <Label>Téléphone du conseiller</Label>
                <Input
                  type="tel"
                  value={offer.advisor_info?.phone || ''}
                  onChange={(e) => setOffer({
                    ...offer,
                    advisor_info: { 
                      name: offer.advisor_info?.name || '',
                      phone: e.target.value,
                      email: offer.advisor_info?.email || ''
                    }
                  })}
                  className="rounded-sm"
                  placeholder="Ex: 09.80.80.23.80"
                />
              </div>
              <div>
                <Label>Email du conseiller</Label>
                <Input
                  type="email"
                  value={offer.advisor_info?.email || ''}
                  onChange={(e) => setOffer({
                    ...offer,
                    advisor_info: { 
                      name: offer.advisor_info?.name || '',
                      phone: offer.advisor_info?.phone || '',
                      email: e.target.value
                    }
                  })}
                  className="rounded-sm"
                  placeholder="Ex: conseiller@placedesenergies.com"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Client Info */}
        <Card className="mb-6 border border-border rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading">Informations client (détails techniques)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom du site</Label>
                <Input
                  data-testid="client-site-name-input"
                  value={offer.client_info.site_name}
                  onChange={(e) => updateClientInfo('site_name', e.target.value)}
                  className="rounded-sm"
                />
              </div>
              {/* Point de livraison - PCE pour gaz, RAE pour électricité */}
              {offer.energy_type === 'electricity' ? (
                <div>
                  <Label>Numéro RAE/PRM</Label>
                  <Input
                    data-testid="client-rae-input"
                    value={offer.client_info.rae_number || ''}
                    onChange={(e) => updateClientInfo('rae_number', e.target.value)}
                    className="rounded-sm"
                  />
                </div>
              ) : (
                <div>
                  <Label>Numéro PCE</Label>
                  <Input
                    data-testid="client-pce-input"
                    value={offer.client_info.pce_number || ''}
                    onChange={(e) => updateClientInfo('pce_number', e.target.value)}
                    className="rounded-sm"
                  />
                </div>
              )}
              {/* Puissance souscrite - uniquement pour électricité */}
              {offer.energy_type === 'electricity' && (
                <div>
                  <Label>Puissance souscrite (kVA)</Label>
                  <Input
                    data-testid="client-power-input"
                    type="number"
                    value={offer.client_info.subscribed_power || ''}
                    onChange={(e) => updateClientInfo('subscribed_power', parseFloat(e.target.value) || 0)}
                    className="rounded-sm"
                  />
                </div>
              )}
              <div>
                <Label>Tarif</Label>
                <Input
                  data-testid="client-tariff-input"
                  value={offer.client_info.tariff || ''}
                  onChange={(e) => updateClientInfo('tariff', e.target.value)}
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label>Profil</Label>
                <Input
                  data-testid="client-profile-input"
                  value={offer.client_info.profile || ''}
                  onChange={(e) => updateClientInfo('profile', e.target.value)}
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label>Consommation (kWh)</Label>
                <Input
                  data-testid="client-consumption-input"
                  type="number"
                  value={offer.client_info.consumption || ''}
                  onChange={(e) => updateClientInfo('consumption', parseFloat(e.target.value) || 0)}
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label>Fin de contrat</Label>
                <Input
                  data-testid="client-contract-end-input"
                  value={offer.client_info.contract_end_date || ''}
                  onChange={(e) => updateClientInfo('contract_end_date', e.target.value)}
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label>Adresse postale</Label>
                <Input
                  value={offer.client_info.address || ''}
                  onChange={(e) => updateClientInfo('address', e.target.value)}
                  className="rounded-sm"
                  placeholder="Ex: 1 rue de la Paix, 75001 Paris"
                />
              </div>
              <div>
                <Label>Fournisseur actuel</Label>
                <Input
                  value={offer.client_info.current_supplier || ''}
                  onChange={(e) => updateClientInfo('current_supplier', e.target.value)}
                  className="rounded-sm"
                  placeholder="Ex: EDF, Engie, Total Energies"
                />
              </div>
              <div>
                <Label>Tarification du contrat</Label>
                <Input
                  value={offer.client_info.contract_type || ''}
                  onChange={(e) => updateClientInfo('contract_type', e.target.value)}
                  className="rounded-sm"
                  placeholder="Ex: FIXE, INDEXE"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Suppliers */}
        <div className="space-y-6">
          <h2 className="text-2xl font-heading">Fournisseurs</h2>
          {offer.suppliers && offer.suppliers.map((supplier, index) => (
            <Card key={index} className="border border-border rounded-sm" data-testid={`supplier-card-${index}`}>
              <CardHeader>
                <CardTitle className="font-heading text-lg">{supplier.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Nom</Label>
                    <Input
                      data-testid={`supplier-${index}-name-input`}
                      value={supplier.name}
                      onChange={(e) => updateSupplier(index, 'name', e.target.value)}
                      className="rounded-sm"
                    />
                  </div>
                  
                  {/* Champs spécifiques GAZ */}
                  {offer.energy_type !== 'electricity' && (
                    <>
                      <div>
                        <Label>Molécule (€/MWh)</Label>
                        <Input
                          data-testid={`supplier-${index}-molecule-input`}
                          type="number"
                          step="0.01"
                          value={supplier.molecule_price}
                          onChange={(e) => updateSupplier(index, 'molecule_price', e.target.value)}
                          className="rounded-sm font-mono-data"
                        />
                      </div>
                      <div>
                        <Label>Terme variable (€/MWh)</Label>
                        <Input
                          data-testid={`supplier-${index}-variable-input`}
                          type="number"
                          step="0.01"
                          value={supplier.variable_term_price}
                          onChange={(e) => updateSupplier(index, 'variable_term_price', e.target.value)}
                          className="rounded-sm font-mono-data"
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Champs spécifiques ÉLECTRICITÉ */}
                  {offer.energy_type === 'electricity' && (
                    <>
                      <div>
                        <Label>HPH (€/MWh)</Label>
                        <Input
                          data-testid={`supplier-${index}-hph-input`}
                          type="number"
                          step="0.01"
                          value={supplier.electricity_prices?.hph || 0}
                          onChange={(e) => {
                            const newPrices = { ...(supplier.electricity_prices || {}), hph: parseFloat(e.target.value) };
                            updateSupplier(index, 'electricity_prices', newPrices);
                          }}
                          className="rounded-sm font-mono-data"
                        />
                      </div>
                      <div>
                        <Label>HCH (€/MWh)</Label>
                        <Input
                          data-testid={`supplier-${index}-hch-input`}
                          type="number"
                          step="0.01"
                          value={supplier.electricity_prices?.hch || 0}
                          onChange={(e) => {
                            const newPrices = { ...(supplier.electricity_prices || {}), hch: parseFloat(e.target.value) };
                            updateSupplier(index, 'electricity_prices', newPrices);
                          }}
                          className="rounded-sm font-mono-data"
                        />
                      </div>
                      <div>
                        <Label>HPE (€/MWh)</Label>
                        <Input
                          data-testid={`supplier-${index}-hpe-input`}
                          type="number"
                          step="0.01"
                          value={supplier.electricity_prices?.hpe || 0}
                          onChange={(e) => {
                            const newPrices = { ...(supplier.electricity_prices || {}), hpe: parseFloat(e.target.value) };
                            updateSupplier(index, 'electricity_prices', newPrices);
                          }}
                          className="rounded-sm font-mono-data"
                        />
                      </div>
                      <div>
                        <Label>HCE (€/MWh)</Label>
                        <Input
                          data-testid={`supplier-${index}-hce-input`}
                          type="number"
                          step="0.01"
                          value={supplier.electricity_prices?.hce || 0}
                          onChange={(e) => {
                            const newPrices = { ...(supplier.electricity_prices || {}), hce: parseFloat(e.target.value) };
                            updateSupplier(index, 'electricity_prices', newPrices);
                          }}
                          className="rounded-sm font-mono-data"
                        />
                      </div>
                      <div>
                        <Label>TURPE (€)</Label>
                        <Input
                          data-testid={`supplier-${index}-turpe-input`}
                          type="number"
                          step="0.01"
                          value={supplier.turpe_budget || 0}
                          onChange={(e) => updateSupplier(index, 'turpe_budget', parseFloat(e.target.value))}
                          className="rounded-sm font-mono-data"
                        />
                      </div>
                      <div>
                        <Label>CSPE (€)</Label>
                        <Input
                          data-testid={`supplier-${index}-cspe-input`}
                          type="number"
                          step="0.01"
                          value={supplier.cspe_budget || 0}
                          onChange={(e) => updateSupplier(index, 'cspe_budget', parseFloat(e.target.value))}
                          className="rounded-sm font-mono-data"
                        />
                      </div>
                    </>
                  )}
                  
                  <div>
                    <Label>Abonnement (€/mois)</Label>
                    <Input
                      data-testid={`supplier-${index}-subscription-input`}
                      type="number"
                      step="0.01"
                      value={supplier.subscription_price}
                      onChange={(e) => updateSupplier(index, 'subscription_price', e.target.value)}
                      className="rounded-sm font-mono-data"
                    />
                  </div>
                  <div>
                    <Label>Total TTC (€)</Label>
                    <Input
                      data-testid={`supplier-${index}-ttc-input`}
                      type="number"
                      step="0.01"
                      value={supplier.total_ttc}
                      onChange={(e) => updateSupplier(index, 'total_ttc', e.target.value)}
                      className="rounded-sm font-mono-data"
                    />
                  </div>
                  <div>
                    <Label>Durée contrat</Label>
                    <Input
                      data-testid={`supplier-${index}-duration-input`}
                      value={supplier.contract_duration}
                      onChange={(e) => updateSupplier(index, 'contract_duration', e.target.value)}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label>Fin de contrat</Label>
                    <Input
                      data-testid={`supplier-${index}-end-date-input`}
                      value={supplier.contract_end_date}
                      onChange={(e) => updateSupplier(index, 'contract_end_date', e.target.value)}
                      className="rounded-sm"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default EditOfferPage;
